import React, { useState } from "react";
import styled from "styled-components";
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    Link,
    Divider,
    Tooltip,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AssignmentIcon from "@material-ui/icons/Assignment";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

const Section = styled(Box)`
    margin-top: 8px;
    margin-bottom: 16px;
`;

const Kbd = styled.code`
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
    padding: 0 6px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.06);
`;

const Muted = styled.span`
    color: #5f6368; /* higher-contrast grey */
`;

const CenterHead = styled(TableCell)`
    text-align: center;
    font-size: 0.875rem;
    white-space: nowrap;
`;

const CenterCell = styled(TableCell)`
    text-align: center;
`;

const Card = styled(Paper)`
    padding: 12px;
    border-radius: 12px;
    height: 100%;
`;

const StepHeader = styled(Box)`
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    & > .icon {
        margin-right: 8px;
    }
`;

const StepList = styled.ul`
    margin: 6px 0 0 0;
    padding: 0;
`;

const StepItem = styled.li`
    list-style: none;
    display: flex;
    align-items: center;
    margin: 6px 0;
`;

const ItemText = styled.span`
    display: inline-flex;
    align-items: center;
    margin-left: 8px;
    gap: 6px;
`;

/** Fully opaque, high-contrast tooltip */
const TooltipDark = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))`
    .MuiTooltip-tooltip {
        background: rgba(33, 33, 33, 1); /* alpha 1 */
        color: #fff;
        font-size: 0.875rem;
        line-height: 1.25rem;
        padding: 8px 10px;
        border-radius: 8px;
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
        max-width: 340px;
    }
    .MuiTooltip-arrow {
        color: rgba(33, 33, 33, 1);
    }
`;

/** Match the "Read more / Show less" style used elsewhere, and force left alignment */
const ToggleLink = styled.button`
    appearance: none;
    border: 0;
    background: transparent;
    padding: 0;
    margin-top: 6px;
    color: #1976d2;
    text-decoration: underline;
    cursor: pointer;
    font: inherit;
    line-height: 1.4;
    display: inline-block;
    text-align: left;
    align-self: flex-start; /* keep it left-aligned even in flex containers */
`;

export default function ExampleUsage({ isFileUpload }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <Accordion
            square
            elevation={0}
            expanded={expanded}
            onChange={(_, v) => setExpanded(v)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-label={expanded ? "Show less (collapses below)" : "Read more (expands below)"}
            >
                <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Box display="flex" alignItems="center">
                        <Typography variant="subtitle1">Quick how-to</Typography>
                        <TooltipDark
                            title={
                                <Box p={0.5}>
                                    <Typography variant="body2">
                                        A <b>recommendation</b> is your target value for a page (Title, Meta
                                        Description, H1, or Destination URL). We check the live site at{" "}
                                        <b>regular intervals</b> (e.g. daily, every 2h/4h) and show what’s implemented
                                        vs. what’s still pending — helpful during migrations and redirect QA.
                                    </Typography>
                                </Box>
                            }
                            arrow
                        >
                            <Box ml={1} display="inline-flex" alignItems="center">
                                <InfoOutlinedIcon fontSize="small" />
                            </Box>
                        </TooltipDark>
                    </Box>

                    <Typography variant="body2" color="textSecondary">
                        Paste Excel columns <Kbd>A</Kbd>–<Kbd>E</Kbd>. Blank cells keep current values; use{" "}
                        <Kbd>&lt;REMOVE-RECOMMENDATION&gt;</Kbd> to clear a field.
                    </Typography>

                    <ToggleLink
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation(); // avoid double-toggle from AccordionSummary
                            setExpanded((v) => !v);
                        }}
                        aria-expanded={expanded}
                    >
                        {expanded ? "Read less" : "Read more"}
                    </ToggleLink>
                </Box>
            </AccordionSummary>

            <AccordionDetails style={{ display: "block" }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Card elevation={0}>
                            <StepHeader>
                                <AssignmentIcon className="icon" />
                                <Typography variant="subtitle2">Step 1 — Prepare your recommendations on a spreadsheet (Excel / LibreOffice..)</Typography>
                            </StepHeader>

                            <Typography variant="body2" component="div">
                                <StepList>
                                    <StepItem>
                                        <Chip size="small" label="A" />
                                        <ItemText>
                      <span>
                        <b>URL</b> <Muted>(required)</Muted>
                      </span>
                                            <TooltipDark
                                                title={
                                                    <Box p={0.5}>
                                                        <Typography variant="body2">
                                                            The page to track at <b>regular intervals</b> (e.g. daily, every
                                                            2h/4h). We’ll compare the live page to your targets and show progress.
                                                        </Typography>
                                                    </Box>
                                                }
                                                arrow
                                            >
                                                <InfoOutlinedIcon fontSize="small" />
                                            </TooltipDark>
                                        </ItemText>
                                    </StepItem>

                                    <StepItem>
                                        <Chip size="small" label="B" />
                                        <ItemText>
                      <span>
                        <b>Destination URL</b> <Muted>(optional)</Muted>
                      </span>
                                            <TooltipDark
                                                title={
                                                    <Box p={0.5}>
                                                        <Typography variant="body2">
                                                            Expected redirect/canonical target. Use to assert that{" "}
                                                            <Kbd>/old</Kbd> should become <Kbd>/new</Kbd>. Leave blank if not
                                                            applicable.
                                                        </Typography>
                                                    </Box>
                                                }
                                                arrow
                                            >
                                                <InfoOutlinedIcon fontSize="small" />
                                            </TooltipDark>
                                        </ItemText>
                                    </StepItem>

                                    <StepItem>
                                        <Chip size="small" label="C" />
                                        <ItemText>
                      <span>
                        <b>Title</b> <Muted>(optional)</Muted>
                      </span>
                                            <TooltipDark
                                                title={
                                                    <Box p={0.5}>
                                                        <Typography variant="body2">
                                                            Desired <Kbd>&lt;title&gt;</Kbd>. Leave blank to keep current; use{" "}
                                                            <Kbd>&lt;REMOVE-RECOMMENDATION&gt;</Kbd> to clear the target.
                                                        </Typography>
                                                    </Box>
                                                }
                                                arrow
                                            >
                                                <InfoOutlinedIcon fontSize="small" />
                                            </TooltipDark>
                                        </ItemText>
                                    </StepItem>

                                    <StepItem>
                                        <Chip size="small" label="D" />
                                        <ItemText>
                      <span>
                        <b>Meta Description</b> <Muted>(optional)</Muted>
                      </span>
                                            <TooltipDark
                                                title={
                                                    <Box p={0.5}>
                                                        <Typography variant="body2">
                                                            Your target meta description. Leave blank to keep; use{" "}
                                                            <Kbd>&lt;REMOVE-RECOMMENDATION&gt;</Kbd> to clear.
                                                        </Typography>
                                                    </Box>
                                                }
                                                arrow
                                            >
                                                <InfoOutlinedIcon fontSize="small" />
                                            </TooltipDark>
                                        </ItemText>
                                    </StepItem>

                                    <StepItem>
                                        <Chip size="small" label="E" />
                                        <ItemText>
                      <span>
                        <b>H1</b> <Muted>(optional)</Muted>
                      </span>
                                            <TooltipDark
                                                title={
                                                    <Box p={0.5}>
                                                        <Typography variant="body2">
                                                            Your target H1 heading. Leave blank to keep; use{" "}
                                                            <Kbd>&lt;REMOVE-RECOMMENDATION&gt;</Kbd> to clear.
                                                        </Typography>
                                                    </Box>
                                                }
                                                arrow
                                            >
                                                <InfoOutlinedIcon fontSize="small" />
                                            </TooltipDark>
                                        </ItemText>
                                    </StepItem>
                                </StepList>
                            </Typography>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card elevation={0}>
                            <StepHeader>
                                <CloudUploadIcon className="icon" />
                                <Typography variant="subtitle2">Step 2 — Paste or upload</Typography>
                            </StepHeader>
                            <Typography variant="body2" component="div">
                                Paste rows into the textarea.
                                {isFileUpload ? " Or upload an .xlsx with one or more sheets." : ""} Leave cells
                                blank to keep existing values unchanged.
                            </Typography>
                            <Box mt={1} display="flex" flexWrap="wrap">
                                <Chip size="small" style={{ marginRight: 8, marginBottom: 6 }} label="Keep value: leave blank" />
                                <Chip
                                    size="small"
                                    style={{ marginBottom: 6 }}
                                    icon={<RemoveCircleOutlineIcon />}
                                    label="Remove value: <REMOVE-RECOMMENDATION>"
                                />
                            </Box>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card elevation={0}>
                            <StepHeader>
                                <CheckCircleOutlineIcon className="icon" />
                                <Typography variant="subtitle2">Step 3 — Review & submit</Typography>
                            </StepHeader>
                            <Typography variant="body2" component="div">
                                Fix any inline validation errors, then click <b>Add</b>. The checker will monitor
                                the live site at <b>regular intervals</b> (e.g. daily, every 2h/4h) and update each
                                URL’s status.
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>

                <Section>
                    <Divider />
                </Section>

                <Section>
                    <Typography variant="subtitle1" gutterBottom>
                        Example (no need to memorise this)
                    </Typography>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <CenterHead width="6%">
                                    <Muted>#</Muted>
                                </CenterHead>
                                <CenterHead>
                                    <Chip size="small" label="A" /> URL
                                </CenterHead>
                                <CenterHead>
                                    <Chip size="small" label="B" /> Destination URL
                                </CenterHead>
                                <CenterHead>
                                    <Chip size="small" label="C" /> Title
                                </CenterHead>
                                <CenterHead>
                                    <Chip size="small" label="D" /> Meta Description
                                </CenterHead>
                                <CenterHead>
                                    <Chip size="small" label="E" /> H1
                                </CenterHead>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            <TableRow>
                                <CenterCell>1</CenterCell>
                                <CenterCell>
                                    <Link href="https://url1" target="_blank" rel="noopener noreferrer">
                                        https://url1
                                    </Link>
                                </CenterCell>
                                <CenterCell>
                                    <Link href="https://destination1" target="_blank" rel="noopener noreferrer">
                                        https://destination1
                                    </Link>
                                </CenterCell>
                                <CenterCell>Example title</CenterCell>
                                <CenterCell>Example Meta Description</CenterCell>
                                <CenterCell>Example H1</CenterCell>
                            </TableRow>

                            <TableRow>
                                <CenterCell>2</CenterCell>
                                <CenterCell>
                                    <Link href="https://url2" target="_blank" rel="noopener noreferrer">
                                        https://url2
                                    </Link>
                                </CenterCell>
                                <CenterCell>
                                    <Muted>—</Muted>
                                </CenterCell>
                                <CenterCell>
                                    <Muted>—</Muted>
                                </CenterCell>
                                <CenterCell>
                                    <Muted>—</Muted>
                                </CenterCell>
                                <CenterCell>
                                    <Kbd>&lt;REMOVE-RECOMMENDATION&gt;</Kbd>
                                </CenterCell>
                            </TableRow>

                            <TableRow>
                                <CenterCell>3</CenterCell>
                                <CenterCell>
                                    <Link href="https://url3" target="_blank" rel="noopener noreferrer">
                                        https://url3
                                    </Link>
                                </CenterCell>
                                <CenterCell>
                                    <Link href="https://destination2" target="_blank" rel="noopener noreferrer">
                                        https://destination2
                                    </Link>
                                </CenterCell>
                                <CenterCell>A title</CenterCell>
                                <CenterCell>A Meta Description</CenterCell>
                                <CenterCell>This is an H1</CenterCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    <Box mt={1}>
                        <Typography variant="body2">
                            Empty values in <Kbd>B2</Kbd>, <Kbd>C2</Kbd>, and <Kbd>D2</Kbd> keep existing
                            recommendations unchanged (or add none if missing). The value in <Kbd>E2</Kbd> removes
                            the existing H1 recommendation if present.
                        </Typography>
                    </Box>
                </Section>
            </AccordionDetails>
        </Accordion>
    );
}
